/**
 * The stage: the engine canvas (exactly what renders) plus an SVG overlay for selection handles,
 * snap guides and framing overlays. Elements not yet on screen at the playhead show as dashed
 * ghosts so they can still be selected and placed.
 */

import {
  apply,
  cameraMatrix,
  chain,
  elementTimes,
  frameRect,
  invert,
  rotate,
  scale,
  sortedScenes,
  translate,
  viewRect,
  type Element,
  type Mat,
  type Project,
  type Rect,
} from "@studio/engine";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { addImage, sceneAt } from "./actions";
import type { Preview } from "./preview";
import { selectedElements, selectElement, setSelectedElements, useEditor } from "./store";

interface Box {
  el: Element;
  corners: [number, number][]; // css px
  center: [number, number];
  live: boolean;
  w: number; // board units at scale 1
  h: number;
}

const SNAP_PX = 7;

export function Stage({ preview }: { preview: Preview }) {
  const wrap = useRef<HTMLDivElement>(null);
  const holder = useRef<HTMLDivElement>(null);
  const [css, setCss] = useState({ w: 960, h: 540 });
  const project = useEditor((s) => s.project)!;
  const time = useEditor((s) => s.time);
  const playing = useEditor((s) => s.playing);
  const selection = useEditor((s) => s.selection);
  const overlays = useEditor((s) => s.overlays);
  const boardView = useEditor((s) => s.boardView);
  const [guides, setGuides] = useState<{ x: number[]; y: number[] }>({ x: [], y: [] });
  const version = useSyncExternalStore(
    (cb) => preview.onChange(cb),
    () => preview.version,
  );

  useEffect(() => {
    holder.current!.appendChild(preview.canvas);
    return () => preview.canvas.remove();
  }, [preview]);

  // fit the output aspect into the available space; canvas resolution follows (capped at the output)
  useEffect(() => {
    const ro = new ResizeObserver(() => {
      const r = wrap.current!.getBoundingClientRect();
      const ar = project.output.width / project.output.height;
      let w = r.width - 24;
      let h = w / ar;
      if (h > r.height - 24) {
        h = r.height - 24;
        w = h * ar;
      }
      w = Math.max(160, Math.floor(w));
      h = Math.round(w / ar);
      setCss({ w, h });
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const pw = Math.min(project.output.width, Math.round(w * dpr));
      preview.setSize(pw, Math.round(pw / ar));
    });
    ro.observe(wrap.current!);
    return () => ro.disconnect();
  }, [preview, project.output.width, project.output.height]);

  const cam = preview.viewCamera(time);
  const M = cameraMatrix(project, cam, css.w, css.h);
  const Minv = invert(M);

  const boxes = useMemo(() => computeBoxes(project, time, M, preview, boardView), [project, time, css.w, css.h, version, boardView]); // eslint-disable-line

  const toBoard = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = holder.current!.getBoundingClientRect();
    return apply(Minv, e.clientX - r.left, e.clientY - r.top);
  };

  const startDrag = (e: React.PointerEvent, box: Box, mode: "move" | "scale" | "rotate") => {
    e.stopPropagation();
    e.preventDefault();
    const st = useEditor.getState();
    if (mode === "move" && (e.shiftKey || e.ctrlKey || e.metaKey)) {
      selectElement(box.el.id, true); // toggle in the multi-selection, no drag
      return;
    }
    const already = selectedElements();
    if (!(mode === "move" && already.includes(box.el.id) && already.length > 1)) st.select({ kind: "element", id: box.el.id });
    const group = mode === "move" ? selectedElements() : [box.el.id];
    if (box.el.locked) return;
    st.begin();
    const p0 = toBoard(e);
    const t0 = { ...box.el.transform };
    const starts = new Map(project.elements.filter((x) => group.includes(x.id) && !x.locked).map((x) => [x.id, { x: x.transform.x, y: x.transform.y }]));
    const pxPerBoard = Math.hypot(M[0], M[1]);
    const move = (ev: PointerEvent) => {
      const p = toBoard(ev);
      if (mode === "move") {
        let x = t0.x + p[0] - p0[0];
        let y = t0.y + p[1] - p0[1];
        if (!ev.altKey) {
          const s = snap(project, box, x - t0.x, y - t0.y, time, SNAP_PX / pxPerBoard, boxes);
          x += s.dx;
          y += s.dy;
          setGuides({ x: s.gx.map((g) => apply(M, g, 0)[0]), y: s.gy.map((g) => apply(M, 0, g)[1]) });
        } else setGuides({ x: [], y: [] });
        const dx = x - t0.x;
        const dy = y - t0.y;
        st.edit((d) => {
          for (const [id, p] of starts) {
            const el = d.elements.find((q) => q.id === id);
            if (!el) continue;
            el.transform.x = round1(p.x + dx);
            el.transform.y = round1(p.y + dy);
          }
        });
      } else if (mode === "scale") {
        const d0 = Math.hypot(p0[0] - t0.x, p0[1] - t0.y);
        const d1 = Math.hypot(p[0] - t0.x, p[1] - t0.y);
        const s = Math.max(0.02, (t0.scale * d1) / Math.max(1, d0));
        st.edit((d) => {
          d.elements.find((q) => q.id === box.el.id)!.transform.scale = Math.round(s * 10000) / 10000;
        });
      } else {
        const a0 = Math.atan2(p0[1] - t0.y, p0[0] - t0.x);
        const a1 = Math.atan2(p[1] - t0.y, p[0] - t0.x);
        let r = t0.rotation + ((a1 - a0) * 180) / Math.PI;
        r = ((r % 360) + 540) % 360 - 180;
        if (ev.shiftKey) r = Math.round(r / 15) * 15;
        st.edit((d) => {
          d.elements.find((q) => q.id === box.el.id)!.transform.rotation = round1(r);
        });
      }
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setGuides({ x: [], y: [] });
      useEditor.getState().end();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onDrop = (e: React.DragEvent) => {
    const id = e.dataTransfer.getData("application/x-studio-asset");
    if (!id) return;
    e.preventDefault();
    e.stopPropagation();
    const a = project.assets.find((x) => x.id === id);
    if (!a || (a.kind !== "image" && a.kind !== "svg")) return;
    addImage(a);
    const [bx, by] = toBoard(e);
    const sel = useEditor.getState().selection;
    if (sel?.kind === "element")
      useEditor.getState().edit((d) => {
        const el = d.elements.find((q) => q.id === sel.id)!;
        el.transform.x = round1(bx);
        el.transform.y = round1(by);
      });
  };

  const multi = useEditor((s) => s.multi);
  const selIds = new Set([...(selection?.kind === "element" ? [selection.id] : []), ...multi]);
  const selected = selection?.kind === "element" && selIds.size === 1 ? boxes.find((b) => b.el.id === selection.id) : undefined;
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  /** Drag on empty stage: box-select the elements it touches (Shift adds to the selection). */
  const startMarquee = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const r = holder.current!.getBoundingClientRect();
    const x0 = e.clientX - r.left;
    const y0 = e.clientY - r.top;
    const add = e.shiftKey;
    const before = add ? selectedElements() : [];
    let moved = false;
    const move = (ev: PointerEvent) => {
      const x1 = ev.clientX - r.left;
      const y1 = ev.clientY - r.top;
      if (Math.abs(x1 - x0) + Math.abs(y1 - y0) > 4) moved = true;
      if (moved) setMarquee({ x0, y0, x1, y1 });
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setMarquee(null);
      if (!moved) {
        if (!add) useEditor.getState().select(null);
        return;
      }
      const x1 = ev.clientX - r.left;
      const y1 = ev.clientY - r.top;
      const [lx, hx, ly, hy] = [Math.min(x0, x1), Math.max(x0, x1), Math.min(y0, y1), Math.max(y0, y1)];
      const hit = boxes
        .filter((b) => !b.el.locked)
        .filter((b) => {
          const xs = b.corners.map((c) => c[0]);
          const ys = b.corners.map((c) => c[1]);
          return Math.max(...xs) >= lx && Math.min(...xs) <= hx && Math.max(...ys) >= ly && Math.min(...ys) <= hy;
        })
        .map((b) => b.el.id);
      setSelectedElements([...new Set([...before, ...hit])]);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div className="stage" ref={wrap} onPointerDown={(e) => { if (e.target === wrap.current) useEditor.getState().select(null); }} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      <div className="stage-frame" style={{ width: css.w, height: css.h }}>
        <div ref={holder} className="canvas-holder" />
        <svg className="overlay" width={css.w} height={css.h} onPointerDown={(e) => { if (e.target === e.currentTarget) startMarquee(e); }}>
          {overlays.thirds && <Thirds w={css.w} h={css.h} />}
          {overlays.safe && <Safe w={css.w} h={css.h} />}
          {!playing &&
            boxes.map((b) => (
              <polygon
                key={b.el.id}
                className={`hit ${b.live ? "live" : "ghost"} ${selIds.has(b.el.id) ? "sel" : ""}`}
                points={b.corners.map((c) => c.join(",")).join(" ")}
                onPointerDown={(e) => startDrag(e, b, "move")}
              >
                <title>{b.el.name || b.el.type}</title>
              </polygon>
            ))}
          {!playing &&
            boxes
              .filter((b) => !b.live)
              .map((b) => (
                <text key={b.el.id + "-l"} className="ghost-label" x={b.corners[0][0] + 4} y={b.corners[0][1] + 14}>
                  {b.el.name || b.el.type}
                </text>
              ))}
          {!playing && selected && <Handles box={selected} onDrag={startDrag} />}
          {marquee && (
            <rect className="marquee" x={Math.min(marquee.x0, marquee.x1)} y={Math.min(marquee.y0, marquee.y1)}
              width={Math.abs(marquee.x1 - marquee.x0)} height={Math.abs(marquee.y1 - marquee.y0)} />
          )}
          {boardView && <BoardOverlay project={project} time={time} M={M} toBoard={toBoard} preview={preview} />}
          {guides.x.map((x, i) => (
            <line key={"gx" + i} className="guide" x1={x} x2={x} y1={0} y2={css.h} />
          ))}
          {guides.y.map((y, i) => (
            <line key={"gy" + i} className="guide" x1={0} x2={css.w} y1={y} y2={y} />
          ))}
        </svg>
      </div>
    </div>
  );
}

function Handles({ box, onDrag }: { box: Box; onDrag: (e: React.PointerEvent, b: Box, m: "move" | "scale" | "rotate") => void }) {
  const [a, b] = box.corners;
  const top: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const dx = top[0] - box.center[0];
  const dy = top[1] - box.center[1];
  const n = Math.hypot(dx, dy) || 1;
  const rot: [number, number] = [top[0] + (dx / n) * 28, top[1] + (dy / n) * 28];
  return (
    <g>
      <line className="rot-stem" x1={top[0]} y1={top[1]} x2={rot[0]} y2={rot[1]} />
      <circle className="handle rot" cx={rot[0]} cy={rot[1]} r={6} onPointerDown={(e) => onDrag(e, box, "rotate")}>
        <title>Rotate (Shift: 15° steps)</title>
      </circle>
      {box.corners.map((c, i) => (
        <rect key={i} className="handle" x={c[0] - 5} y={c[1] - 5} width={10} height={10} onPointerDown={(e) => onDrag(e, box, "scale")}>
          <title>Scale</title>
        </rect>
      ))}
    </g>
  );
}

function Thirds({ w, h }: { w: number; h: number }) {
  return (
    <g className="thirds">
      {[1, 2].map((k) => (
        <g key={k}>
          <line x1={(w * k) / 3} x2={(w * k) / 3} y1={0} y2={h} />
          <line y1={(h * k) / 3} y2={(h * k) / 3} x1={0} x2={w} />
        </g>
      ))}
    </g>
  );
}

function Safe({ w, h }: { w: number; h: number }) {
  return (
    <g className="safe">
      <rect x={w * 0.05} y={h * 0.05} width={w * 0.9} height={h * 0.9}>
        <title>Action safe (90%)</title>
      </rect>
      <rect x={w * 0.1} y={h * 0.1} width={w * 0.8} height={h * 0.8}>
        <title>Title safe (80%)</title>
      </rect>
    </g>
  );
}

function elementLocal(el: Element): Mat {
  const t = el.transform;
  return chain(translate(t.x, t.y), rotate(t.rotation), scale(t.scale * (t.flipX ? -1 : 1), t.scale * (t.flipY ? -1 : 1)));
}

function computeBoxes(p: Project, t: number, M: Mat, preview: Preview, all = false): Box[] {
  const times = elementTimes(p);
  const scene = sceneAt(p, t);
  const out: Box[] = [];
  for (const el of [...p.elements].sort((a, b) => a.z - b.z)) {
    if (el.hidden) continue;
    const tm = times.get(el.id)!;
    const live = t >= tm.enter0 && t < tm.end;
    if (!live && el.sceneId !== scene?.id && !all) continue;
    let size = preview.elementSize(el.id);
    if (!size) {
      const a = el.image ? p.assets.find((x) => x.id === el.image!.assetId) : undefined;
      size = { w: a?.width ?? 200, h: a?.height ?? 120 };
    }
    const L = chain(M, elementLocal(el));
    const hw = size.w / 2;
    const hh = size.h / 2;
    const corners = ([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]] as const).map(([x, y]) => apply(L, x, y));
    out.push({ el, corners, center: apply(L, 0, 0), live, w: size.w, h: size.h });
  }
  return out;
}

/** Snap a move (dx, dy in board units) to the frame and other elements; returns the correction. */
function snap(p: Project, box: Box, dx: number, dy: number, t: number, tol: number, boxes: Box[]) {
  const ext = (b: Box, ox = 0, oy = 0) => {
    const t_ = b.el.transform;
    const L = elementLocal({ ...b.el, transform: { ...t_, x: t_.x + ox, y: t_.y + oy } });
    const pts = ([[-b.w / 2, -b.h / 2], [b.w / 2, -b.h / 2], [b.w / 2, b.h / 2], [-b.w / 2, b.h / 2]] as const).map(([x, y]) => apply(L, x, y));
    const xs = pts.map((q) => q[0]);
    const ys = pts.map((q) => q[1]);
    return { x: [Math.min(...xs), (Math.min(...xs) + Math.max(...xs)) / 2, Math.max(...xs)], y: [Math.min(...ys), (Math.min(...ys) + Math.max(...ys)) / 2, Math.max(...ys)] };
  };
  const me = ext(box, dx, dy);
  const fr = frameRect(p, sceneAt(p, t));
  const tx = [fr.x, fr.x + fr.w / 2, fr.x + fr.w];
  const ty = [fr.y, fr.y + fr.h / 2, fr.y + fr.h];
  for (const b of boxes) {
    if (b.el.id === box.el.id) continue;
    const e = ext(b);
    tx.push(...e.x);
    ty.push(...e.y);
  }
  const best = (mine: number[], targets: number[]) => {
    let d = Infinity;
    let g: number | null = null;
    for (const m of mine)
      for (const tg of targets)
        if (Math.abs(tg - m) < Math.abs(d) && Math.abs(tg - m) <= tol) {
          d = tg - m;
          g = tg;
        }
    return g === null ? { d: 0, g: [] as number[] } : { d, g: [g] };
  };
  const bx = best(me.x, tx);
  const by = best(me.y, ty);
  return { dx: bx.d, dy: by.d, gx: bx.g, gy: by.g };
}

const round1 = (v: number) => Math.round(v * 10) / 10;

// ------------------------------------------------------------------------------------- board view

/** Scene frames, the live camera and the selected camera key, editable on the zoomed-out board. */
function BoardOverlay({ project, time, M, toBoard, preview }: { project: Project; time: number; M: Mat; toBoard: (e: { clientX: number; clientY: number }) => [number, number]; preview: Preview }) {
  const selection = useEditor((s) => s.selection);
  const ar = project.output.width / project.output.height;
  const poly = (r: Rect) => [apply(M, r.x, r.y), apply(M, r.x + r.w, r.y), apply(M, r.x + r.w, r.y + r.h), apply(M, r.x, r.y + r.h)];
  const pts = (r: Rect) => poly(r).map((c) => c.join(",")).join(" ");

  /** drag a rect: move from inside, resize (keeping the output aspect) from a corner */
  const dragRect = (e: React.PointerEvent, r0: Rect, mode: "move" | "size", commit: (r: Rect) => void) => {
    e.stopPropagation();
    e.preventDefault();
    const st = useEditor.getState();
    st.begin();
    const p0 = toBoard(e);
    const cx = r0.x + r0.w / 2;
    const cy = r0.y + r0.h / 2;
    const move = (ev: PointerEvent) => {
      const p = toBoard(ev);
      if (mode === "move") commit({ ...r0, x: Math.round(r0.x + p[0] - p0[0]), y: Math.round(r0.y + p[1] - p0[1]) });
      else {
        const k = Math.max(0.1, Math.hypot(p[0] - cx, p[1] - cy) / Math.max(1, Math.hypot(p0[0] - cx, p0[1] - cy)));
        const w = Math.round(Math.max(192, r0.w * k));
        const h = Math.round(w / ar);
        commit({ x: Math.round(cx - w / 2), y: Math.round(cy - h / 2), w, h });
      }
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      useEditor.getState().end();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const scenes = sortedScenes(project);
  const live = viewRect(project, preview.cameraAt(time));
  const key = selection?.kind === "camkey" ? project.camera.keyframes.find((k) => k.id === selection.id) : undefined;
  const keyRect = key ? viewRect(project, key) : null;
  const boardR: Rect = { x: 0, y: 0, w: project.board.width, h: project.board.height };

  return (
    <g>
      <polygon className="board-edge" points={pts(boardR)} />
      {scenes.map((s) => {
        const r = frameRect(project, s);
        const sel = selection?.kind === "scene" && selection.id === s.id;
        const c = poly(r);
        return (
          <g key={s.id}>
            <polygon className={`scene-frame ${sel ? "sel" : ""}`} points={pts(r)}
              onPointerDown={(e) => {
                useEditor.getState().select({ kind: "scene", id: s.id });
                dragRect(e, r, "move", (nr) => useEditor.getState().edit((d) => void (d.scenes.find((x) => x.id === s.id)!.frame = nr)));
              }} />
            <text className="frame-label" x={c[0][0] + 4} y={c[0][1] + 13}>{s.number ? `${s.number} · ` : ""}{s.name}</text>
            {sel && c.map((q, i) => (
              <rect key={i} className="handle" x={q[0] - 5} y={q[1] - 5} width={10} height={10}
                onPointerDown={(e) => dragRect(e, r, "size", (nr) => useEditor.getState().edit((d) => void (d.scenes.find((x) => x.id === s.id)!.frame = nr)))} />
            ))}
          </g>
        );
      })}
      <polygon className="camera-live" points={pts(live)} />
      {key && keyRect && (
        <g>
          <polygon className="camera-key" points={pts(keyRect)}
            onPointerDown={(e) => dragRect(e, keyRect, "move", (nr) => editKey(key.id, nr))} />
          {poly(keyRect).map((q, i) => (
            <rect key={i} className="handle" x={q[0] - 5} y={q[1] - 5} width={10} height={10} onPointerDown={(e) => dragRect(e, keyRect, "size", (nr) => editKey(key.id, nr))} />
          ))}
        </g>
      )}
    </g>
  );

  function editKey(id: string, r: Rect) {
    useEditor.getState().edit((d) => {
      const k = d.camera.keyframes.find((x) => x.id === id);
      if (!k) return;
      k.x = Math.round(r.x + r.w / 2);
      k.y = Math.round(r.y + r.h / 2);
      k.zoom = +(project.output.width / r.w).toFixed(3);
    });
  }
}
